import { getOr } from 'lodash/fp';
import React, { useCallback, useEffect } from 'react';

interface StickyStyles {
  top?: string;
  zIndex?: string;
}

export const useSticky = (enableSticky: boolean, stickyStyles?: StickyStyles) => {
  const stickyRef = React.useRef<HTMLDivElement>(null);

  const addStickyStyles = useCallback(() => {
    if (!stickyRef?.current?.style) return;
    stickyRef.current.style.top = getOr('-2px', 'top', stickyStyles);
    stickyRef.current.style.position = 'sticky';
    stickyRef.current.style.zIndex = getOr('1100', 'zIndex', stickyStyles);
  }, [stickyStyles]);

  const removeStickyStyles = useCallback(() => {
    if (!stickyRef?.current?.style) return;
    stickyRef.current.style.removeProperty('top');
    stickyRef.current.style.removeProperty('position');
    stickyRef.current.style.removeProperty('zIndex');
  }, []);

  React.useEffect(() => {
    if (enableSticky) {
      addStickyStyles();
      return;
    }

    removeStickyStyles();
  }, [enableSticky, addStickyStyles, removeStickyStyles]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => e.target.toggleAttribute('data-stuck', e.intersectionRatio < 1),
      { threshold: [1] },
    );
    if (stickyRef.current) {
      observer.observe(stickyRef.current);
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (stickyRef.current) observer.unobserve(stickyRef.current);
    };
  }, []);

  return { stickyRef };
};
